/**
 * NotificationsBanner
 * ─────────────────────
 * Top banner shown on /subjects when the student hasn't yet enabled
 * browser push notifications. Replaces the static card on /profile so
 * the prompt is a recurring nudge rather than a one-time visit.
 *
 * Behaviour:
 *   - Only fires when getPushState() === 'default' (true first-time
 *     state). 'granted' / 'denied' / 'unsupported' suppress the banner.
 *   - Dismissible with ✕. Up to 2 dismissals per day; on the second
 *     dismissal the banner stops appearing until the next day. Day
 *     boundary is local-time YYYY-MM-DD.
 *   - "Activar" calls subscribeToPush() directly. Per Web Push spec the
 *     permission prompt MUST fire from a user gesture, so the banner
 *     attaches to a click and never auto-fires permission.
 *
 * 2026-04-28 - replaces the static NotificationSettings card on /profile.
 */

import React, { useState } from 'react'
import { getPushState, subscribeToPush, isPushSupported } from '../lib/push'

const STORAGE_KEY = 'notif_banner_state'
const DAILY_DISMISSAL_CAP = 2

interface BannerState {
  date:        string  // YYYY-MM-DD (local)
  dismissals:  number
}

function readState(): BannerState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { date: localToday(), dismissals: 0 }
    const parsed = JSON.parse(raw) as BannerState
    if (parsed.date !== localToday()) return { date: localToday(), dismissals: 0 }
    return parsed
  } catch {
    return { date: localToday(), dismissals: 0 }
  }
}

function writeState(s: BannerState): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)) } catch {}
}

function localToday(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Three gates, all synchronous:
 *   1. browser supports push (getPushState !== 'unsupported')
 *   2. permission is in the 'default' (actionable) state
 *   3. dismissal cap not yet hit for today
 */
function shouldShow(): boolean {
  if (!isPushSupported())           return false
  if (getPushState() !== 'default') return false
  return readState().dismissals < DAILY_DISMISSAL_CAP
}

export const NotificationsBanner: React.FC = () => {
  // Resolved during the first render, NOT in an effect. Every input above is
  // synchronous (matchMedia, Notification.permission, localStorage), so there
  // is no reason to paint the page and then insert a banner into it. Doing it
  // in useEffect shifts everything below down after first paint, and CLS on
  // /subjects is already 0.32 in Clarity, where anything over 0.25 is rated
  // poor. A banner that appears late is a banner that pushes the grid the
  // student is already reaching for.
  const [visible, setVisible] = useState<boolean>(shouldShow)
  const [busy, setBusy]       = useState(false)
  // PWA-mode push subscription has multiple silent-null failure paths
  // (no SW, VAPID 503, iOS version unsupported). Surfacing a short
  // reason here is what tells the student and us why "Activar" looked
  // like it just reset (2026-05-06).
  const [error, setError]     = useState<string | null>(null)

  const handleEnable = async () => {
    setBusy(true)
    setError(null)
    try {
      const sub = await subscribeToPush()
      if (sub) {
        setVisible(false)
        return
      }
      // subscribeToPush returned null. Diagnose: which gate failed?
      const state = getPushState()
      if (state === 'denied') {
        setError('Permissão recusada. Activa as notificações nas definições do telemóvel.')
        setVisible(false)
        return
      }
      if (state === 'unsupported') {
        setError('O teu navegador não suporta notificações.')
        setVisible(false)
        return
      }
      // 'default' + null result: SW not registered, VAPID key unavailable,
      // or another transient failure inside lib/push.ts. Most common in
      // installed PWA: SW race on cold start or backend missing the
      // VAPID env var. Surface a generic retry hint and log so we can
      // dig in via the device's remote debugger.
      // eslint-disable-next-line no-console
      console.warn('[notifs] subscribeToPush returned null - push state:', state, 'SW registered:', !!(await navigator.serviceWorker.getRegistration?.()))
      setError('Não foi possível activar agora. Tenta de novo.')
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[notifs] subscribeToPush threw:', err)
      setError(err?.message ? `Erro: ${err.message}` : 'Não foi possível activar agora.')
    } finally {
      setBusy(false)
    }
  }

  const handleDismiss = () => {
    const next: BannerState = {
      date:       localToday(),
      dismissals: readState().dismissals + 1,
    }
    writeState(next)
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div className="mb-5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3">
      <span className="text-2xl shrink-0 mt-0.5">🔔</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-blue-800 leading-snug">
          Lembretes diários para manteres a tua sequência. Funciona mesmo com dados desligados.
        </p>
        {error && (
          <p className="text-xs text-red-600 leading-snug mt-1.5">{error}</p>
        )}
        <button
          onClick={handleEnable}
          disabled={busy}
          className="mt-2 text-xs font-semibold bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? 'A activar…' : 'Activar'}
        </button>
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Dispensar"
        className="text-blue-400 hover:text-blue-600 text-lg leading-none shrink-0"
      >
        ✕
      </button>
    </div>
  )
}
